/**
 * Czy szereg czasowy pokrywa okno, o które zapytaliśmy?
 *
 * POWÓD ISTNIENIA — defekt znaleziony przez dual-run, nie przez przeglądanie kodu.
 * Ekran „Dostawy" podpisywał wykres punktualności zdaniem „42 dni w oknie · 42 z pomiarem ·
 * 0 bez dostaw", podczas gdy oknem zapytania był CAŁY ROK. Liczba 42 to nie „dni w oknie",
 * tylko „dni, w których w ogóle była jakaś dostawa" — backend robi GROUP BY po dacie i nie
 * tworzy pustych kubełków (brak `generate_series`), więc dni bez dostaw po prostu nie istnieją
 * w odpowiedzi. Skutek: czytelnik dostawał wykres, z którego wynikało, że okno miało 42 dni
 * i w każdym coś dowieziono.
 *
 * Ta sama przyczyna sprawia, że gałąź „dzień bez dostaw" w wykresie jest przy TYM backendzie
 * nieosiągalna: skoro punkt nie istnieje, nie ma czego narysować na jasno. Zostawiamy ją —
 * jest tania i poprawna, gdyby backend zaczął kiedyś wypełniać zera — ale nie udajemy, że
 * cokolwiek dziś mierzy.
 *
 * Liczymy TYLKO z danych, które przychodzą w odpowiedzi. Żadnej zmiany po stronie backendu
 * Drive: ten kod jedzie na produkcję osobnym workflow i nie jest to zakres R5-15.
 */

export interface ZakresSerii {
  date_from: string;
  date_to: string;
}

export interface PunktSerii {
  date: string;
  deliveries: number;
}

export interface PokrycieSerii {
  /** Długość okna zapytania w dniach, liczona włącznie z oboma krańcami. */
  dniWOknie: number | null;
  /** Punkty, w których faktycznie coś dowieziono. */
  dniZDostawami: number;
  /** Punkty OBECNE w serii, ale z zerem dostaw. Przy obecnym backendzie zawsze 0. */
  dniZZerem: number;
  /** Dni okna, których seria w ogóle nie zawiera. */
  dniNieobecne: number | null;
  /** Czy seria pokrywa całe okno dzień po dniu. */
  pelnePokrycie: boolean;
}

const DZIEN_MS = 86_400_000;

function naDate(s: string): number | null {
  // Bierzemy wyłącznie część kalendarzową. Znacznik strefy w tych odpowiedziach jest artefaktem
  // serializacji sterownika bazy, a nie informacją o strefie — doklejanie go tutaj wprowadziłoby
  // przesunięcie o dobę przy krańcach okna.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function policzPokrycie(zakres: ZakresSerii | null | undefined, punkty: readonly PunktSerii[]): PokrycieSerii {
  const dniZDostawami = punkty.filter((p) => p.deliveries > 0).length;
  const dniZZerem = punkty.length - dniZDostawami;

  const od = zakres ? naDate(zakres.date_from) : null;
  const doo = zakres ? naDate(zakres.date_to) : null;
  if (od === null || doo === null || doo < od) {
    return { dniWOknie: null, dniZDostawami, dniZZerem, dniNieobecne: null, pelnePokrycie: false };
  }

  const dniWOknie = Math.round((doo - od) / DZIEN_MS) + 1;
  const dniNieobecne = Math.max(dniWOknie - punkty.length, 0);
  return { dniWOknie, dniZDostawami, dniZZerem, dniNieobecne, pelnePokrycie: dniNieobecne === 0 };
}

/**
 * Podpis pod wykresem. Mówi osobno o oknie i o serii, bo to dwie różne wielkości —
 * mylenie ich było właśnie tym defektem.
 */
export function opiszPokrycie(p: PokrycieSerii): string {
  const czesci: string[] = [`${p.dniZDostawami} dni z dostawami`];
  if (p.dniZZerem > 0) czesci.push(`${p.dniZZerem} dni z zerem dostaw`);
  if (p.dniWOknie !== null) czesci.push(`okno ${p.dniWOknie} dni`);
  if (p.dniNieobecne !== null && p.dniNieobecne > 0) {
    czesci.push(`${p.dniNieobecne} dni okna nie ma w serii — w tych dniach nie było ani jednej dostawy`);
  }
  return czesci.join(' · ');
}
